import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Trash2, Edit3, Plus, Ban, CheckCircle2, X,
  Newspaper, Users, MessageSquare, Tag,
  ShieldCheck, BarChart3, BarChart2, Rocket, Star
} from "lucide-react";
import { Link } from "react-router-dom";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "news", label: "News", icon: Newspaper },
  { id: "users", label: "Users", icon: Users },
  { id: "community", label: "Community", icon: MessageSquare },
  { id: "categories", label: "Categories", icon: Tag },
  { id: "polls", label: "Polls", icon: BarChart2 },
  { id: "projects", label: "Projects", icon: Rocket },
  { id: "predictions", label: "Predictions", icon: Star },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");

  return (
    <div className="pt-28 pb-20 max-w-[1400px] mx-auto px-6 md:px-10" data-testid="admin-dashboard">
      <div className="flex items-center gap-3 mb-2">
        <span className="font-mono text-xs text-yellow-400 uppercase tracking-[0.3em]">/admin</span>
      </div>
      <h1 className="font-display font-bold text-5xl md:text-6xl text-[var(--text-primary)] tracking-tighter">Control Room</h1>
      <p className="text-[var(--text-secondary)] mt-2">Manage content, users, and site settings.</p>

      <div className="mt-10 flex border-b border-[var(--border-soft)] gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-widest font-mono transition whitespace-nowrap ${
                tab === t.id
                  ? "text-yellow-400 border-b-2 border-yellow-400"
                  : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-8">
        {tab === "overview" && <OverviewAdmin setTab={setTab} />}
        {tab === "news" && <NewsAdmin />}
        {tab === "users" && <UsersAdmin />}
        {tab === "community" && <CommunityAdmin />}
        {tab === "categories" && <CategoriesAdmin />}
        {tab === "polls" && <PollsAdmin />}
        {tab === "predictions" && <PredictionsAdmin />}
      </div>
    </div>
  );
}

// ---------- Overview ----------
function OverviewAdmin({ setTab }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cards = stats
    ? [
        { label: "Total Articles", value: stats.articles, color: "text-yellow-400" },
        { label: "Total Users", value: stats.users, color: "text-blue-400" },
        { label: "Community Posts", value: stats.posts, color: "text-emerald-400" },
        { label: "Comments", value: stats.comments, color: "text-purple-400" },
        { label: "Banned Users", value: stats.banned, color: "text-red-400" },
      ]
    : [];

  return (
    <div>
      <h2 className="font-display font-bold text-2xl text-[var(--text-primary)] mb-6">Site Overview</h2>
      {!stats ? (
        <p className="text-[var(--text-secondary)] font-mono text-sm">Loading stats...</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {cards.map((c) => (
            <div key={c.label} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
              <div className={`font-display font-bold text-4xl ${c.color}`}>{c.value}</div>
              <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mt-2">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6">
        <h3 className="font-display font-bold text-xl text-[var(--text-primary)] mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setTab("news")} className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-5 py-2 hover:bg-yellow-500 text-sm">
            + Write Article
          </button>
          <button onClick={() => setTab("users")} className="border border-[var(--border-soft)] text-[var(--text-primary)] font-mono uppercase tracking-wide px-5 py-2 hover:border-yellow-400 hover:text-yellow-400 text-sm">
            Manage Users
          </button>
          <button onClick={() => setTab("categories")} className="border border-[var(--border-soft)] text-[var(--text-primary)] font-mono uppercase tracking-wide px-5 py-2 hover:border-yellow-400 hover:text-yellow-400 text-sm">
            Manage Categories
          </button>
          <button onClick={() => setTab("polls")} className="border border-[var(--border-soft)] text-[var(--text-primary)] font-mono uppercase tracking-wide px-5 py-2 hover:border-yellow-400 hover:text-yellow-400 text-sm">
            Manage Polls
          </button>
          <button onClick={() => setTab("projects")} className="border border-[var(--border-soft)] text-[var(--text-primary)] font-mono uppercase tracking-wide px-5 py-2 hover:border-yellow-400 hover:text-yellow-400 text-sm">
            Manage Projects
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- News ----------
function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const blank = { title: "", excerpt: "", content: "", image_url: "", category_slug: "", featured: false, tags: [], sources: [] };
  const [form, setForm] = useState(blank);
  const [preview, setPreview] = useState(false);

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/news?limit=100"), api.get("/categories")]);
    setItems(a.data);
    setCats(c.data);
  };
  useEffect(() => { load(); }, []);

    const addSource = () => setForm({ ...form, sources: [...(form.sources || []), { label: "", url: "" }] });
  const removeSource = (i) => setForm({ ...form, sources: form.sources.filter((_, idx) => idx !== i) });
  const updateSource = (i, field, val) => {
    const updated = [...form.sources];
    updated[i] = { ...updated[i], [field]: val };
    setForm({ ...form, sources: updated });
  };


  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        tags: typeof form.tags === "string"
          ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : form.tags,
      };
      if (editing) await api.put(`/news/${editing}`, payload);
      else await api.post("/news", payload);
      toast.success(editing ? "Article updated" : "Article published");
      setEditing(null);
      setForm(blank);
      setPreview(false);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const startEdit = (a) => {
    setEditing(a.id);
    setForm({ ...a, tags: (a.tags || []).join(", "), sources: a.sources || [] });
    window.scrollTo({ top: 200, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/news/${id}`);
    toast.success("Article deleted");
    load();
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
      <form onSubmit={save} className="xl:col-span-6 bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6 space-y-4" data-testid="admin-news-form">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-xl text-[var(--text-primary)]">
            {editing ? "Edit Article" : "New Article"}
          </h3>
          <div className="flex items-center gap-2">
            {form.image_url && (
              <button type="button" onClick={() => setPreview(!preview)} className="text-xs font-mono text-zinc-400 hover:text-yellow-400 uppercase tracking-wide">
                {preview ? "Hide Preview" : "Preview Image"}
              </button>
            )}
            {editing && (
              <button type="button" onClick={() => { setEditing(null); setForm(blank); setPreview(false); }} className="text-zinc-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {preview && form.image_url && (
          <img src={form.image_url} alt="preview" className="w-full h-40 object-cover border border-white/10" />
        )}

        <div className="space-y-1">
          <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Title *</label>
          <input data-testid="admin-news-title" required placeholder="Article title..." value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-[var(--bg-base)] border border-[var(--border-soft)] px-3 py-2 text-[var(--text-primary)] focus:border-yellow-400 outline-none" />
        </div>

        <div className="space-y-1">
          <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widests">Image URL</label>
          <input data-testid="admin-news-image" placeholder="https://..." value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full bg-[var(--bg-base)] border border-[var(--border-soft)] px-3 py-2 text-[var(--text-primary)] focus:border-yellow-400 outline-none" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widests">Category</label>
            <select data-testid="admin-news-category" value={form.category_slug} onChange={(e) => setForm({ ...form, category_slug: e.target.value })} className="w-full bg-[var(--bg-base)] border border-[var(--border-soft)] px-3 py-2 text-[var(--text-primary)] focus:border-yellow-400 outline-none">
              <option value="">— Select —</option>
              {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widests">Tags</label>
            <input data-testid="admin-news-tags" placeholder="BTC, ETF, macro" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="w-full bg-[var(--bg-base)] border border-[var(--border-soft)] px-3 py-2 text-[var(--text-primary)] focus:border-yellow-400 outline-none" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Sources & References</label>
            <button type="button" onClick={addSource} className="text-xs font-mono text-yellow-400 hover:underline">+ Add Source</button>
          </div>
          {(form.sources || []).map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <input placeholder="Label (e.g. SEC)" value={s.label} onChange={(e) => updateSource(i, "label", e.target.value)} className="w-32 bg-[var(--bg-base)] border border-[var(--border-soft)] px-2 py-2 text-sm text-[var(--text-primary)] focus:border-yellow-400 outline-none" />
              <input placeholder="https://..." value={s.url} onChange={(e) => updateSource(i, "url", e.target.value)} className="flex-1 bg-[var(--bg-base)] border border-[var(--border-soft)] px-2 py-2 text-sm text-[var(--text-primary)] focus:border-yellow-400 outline-none" />
              <button type="button" onClick={() => removeSource(i)} className="text-zinc-500 hover:text-red-400 transition"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <div className="space-y-1">
          <label className="text-xs font-mono text-zinc-500 uppercase tracking-widests">Excerpt</label>
          <textarea data-testid="admin-news-excerpt" placeholder="Short summary shown on cards..." rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full bg-[#050505] border border-white/20 px-3 py-2 text-white focus:border-yellow-400 outline-none resize-none" />
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widests">Content *</label>
            <span className="text-xs font-mono text-zinc-600">{form.content.length} chars</span>
          </div>
          <textarea data-testid="admin-news-content" required placeholder="Full article content..." rows={12} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="w-full bg-[var(--bg-base)] border border-[var(--border-soft)] px-3 py-2 text-[var(--text-primary)] focus:border-yellow-400 outline-none resize-y font-mono text-sm leading-relaxed" />
        </div>

        <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
          <input type="checkbox" data-testid="admin-news-featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-yellow-400" />
          Feature on homepage hero
        </label>

        <button data-testid="admin-news-save" type="submit" className="w-full bg-yellow-400 text-black font-bold uppercase tracking-wide px-5 py-3 hover:bg-yellow-500 transition">
          <Plus className="inline w-4 h-4 mr-1" />
          {editing ? "Update Article" : "Publish Article"}
        </button>
      </form>

      <div className="xl:col-span-6 space-y-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-xl text-[var(--text-primary)]">
            All Articles <span className="text-[var(--text-tertiary)] text-base">({items.length})</span>
          </h3>
        </div>
        {items.map((a) => (
          <div key={a.id} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-4 flex items-center gap-4 hover:border-yellow-400/20 transition" data-testid={`admin-news-row-${a.id}`}>
            {a.image_url ? <img src={a.image_url} alt="" className="w-16 h-16 object-cover flex-shrink-0" /> : <div className="w-16 h-16 bg-[var(--bg-elevated)] flex-shrink-0 flex items-center justify-center"><Newspaper className="w-5 h-5 text-zinc-700" /></div>}
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-[var(--text-primary)] truncate">{a.title}</div>
              <div className="text-xs font-mono text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                {a.category_slug && <span className="text-yellow-400/70 uppercase">{a.category_slug}</span>}
                {a.featured && <span className="text-emerald-400">★ FEATURED</span>}
                <span>{new Date(a.created_at).toLocaleDateString()}</span>
                <span>{a.views || 0} views</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => startEdit(a)} className="text-zinc-400 hover:text-yellow-400 transition"><Edit3 className="w-4 h-4" /></button>
              <button onClick={() => remove(a.id)} className="text-zinc-400 hover:text-red-400 transition"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Users ----------
function UsersAdmin() {
  const [users, setUsers] = useState([]);

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const ban = async (u) => {
    await api.post(`/admin/users/${u.id}/${u.banned ? "unban" : "ban"}`);
    toast.success(u.banned ? "User unbanned" : "User banned");
    load();
  };

  const promote = async (u) => {
    if (!window.confirm(`Promote ${u.email} to admin?`)) return;
    await api.post(`/admin/users/${u.id}/promote`);
    toast.success("User promoted to admin");
    load();
  };

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    await api.delete(`/admin/users/${u.id}`);
    toast.success("User deleted");
    load();
  };

  return (
    <div data-testid="admin-users-table">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-xl text-[var(--text-primary)]">
          All Users <span className="text-[var(--text-tertiary)] text-base">({users.length})</span>
        </h3>
      </div>
      <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] border-b border-[var(--border-soft)]">
            <tr>
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Role</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Joined</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[var(--border-soft)] hover:bg-[var(--bg-elevated)] transition">
                <td className="p-4 text-[var(--text-primary)] font-mono text-xs">{u.email}</td>
                <td className="p-4 text-[var(--text-secondary)]">{u.name}</td>
                <td className="p-4">
                  <span className={`font-mono text-xs uppercase px-2 py-1 ${u.role === "admin" ? "bg-yellow-400/10 text-yellow-400" : "bg-zinc-800 text-zinc-400"}`}>{u.role}</span>
                </td>
                <td className="p-4">
                  {u.banned ? <span className="text-red-400 text-xs font-mono bg-red-400/10 px-2 py-1">BANNED</span> : <span className="text-emerald-400 text-xs font-mono bg-emerald-400/10 px-2 py-1">ACTIVE</span>}
                </td>
                <td className="p-4 text-zinc-500 text-xs font-mono">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="p-4">
                  {u.role !== "admin" && (
                    <div className="flex justify-end gap-2">
                      <button onClick={() => promote(u)} title="Promote to admin" className="text-zinc-400 hover:text-yellow-400 transition"><ShieldCheck className="w-4 h-4" /></button>
                      <button onClick={() => ban(u)} title={u.banned ? "Unban" : "Ban"} className="text-zinc-400 hover:text-yellow-400 transition">{u.banned ? <CheckCircle2 className="w-4 h-4" /> : <Ban className="w-4 h-4" />}</button>
                      <button onClick={() => del(u)} title="Delete user" className="text-zinc-400 hover:text-red-400 transition"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Community ----------
function CommunityAdmin() {
  const [posts, setPosts] = useState([]);

  const load = async () => {
    const { data } = await api.get("/community/posts?limit=500");
    setPosts(data);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this post and all its comments?")) return;
    await api.delete(`/community/posts/${id}`);
    toast.success("Post deleted");
    load();
  };

  return (
    <div data-testid="admin-community-list">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-xl text-[var(--text-primary)]">
          Community Posts <span className="text-[var(--text-tertiary)] text-base">({posts.length})</span>
        </h3>
      </div>
      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-4 flex items-start gap-4 hover:border-yellow-400/20 transition">
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-[var(--text-primary)]">{p.title}</div>
              <div className="text-xs font-mono text-zinc-500 mt-1 flex items-center gap-3">
                <span>By <Link to={"/profile/" + p.author_id} className="text-zinc-300 hover:text-yellow-400 transition">{p.author_name}</Link></span>
                <span>{new Date(p.created_at).toLocaleString()}</span>
                <span className="text-red-400">❤ {p.likes_count}</span>
                <span className="text-blue-400">💬 {p.comments_count}</span>
              </div>
              <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{p.content}</p>
            </div>
            <button onClick={() => del(p.id)} className="text-zinc-400 hover:text-red-400 transition flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {posts.length === 0 && <p className="text-zinc-500 font-mono text-sm">No community posts yet.</p>}
      </div>
    </div>
  );
}

// ---------- Categories ----------
function CategoriesAdmin() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const load = async () => {
    const { data } = await api.get("/categories");
    setCats(data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/categories", { name, description: desc });
      toast.success("Category added");
      setName(""); setDesc("");
      load();
    } catch (_) { toast.error("Could not add — may already exist"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await api.delete(`/categories/${id}`);
    toast.success("Category deleted");
    load();
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
      <form onSubmit={add} className="xl:col-span-6 bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6 space-y-4">
        <h3 className="font-display font-bold text-xl text-[var(--text-primary)]">New Category</h3>
        <div className="space-y-1">
          <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widests">Name *</label>
          <input required placeholder="e.g. Layer 2" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-[var(--bg-base)] border border-[var(--border-soft)] px-3 py-2 text-[var(--text-primary)] focus:border-yellow-400 outline-none" />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widests">Description</label>
          <textarea placeholder="Brief description..." rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} className="w-full bg-[var(--bg-base)] border border-[var(--border-soft)] px-3 py-2 text-[var(--text-primary)] focus:border-yellow-400 outline-none resize-none" />
        </div>
        <button type="submit" className="w-full bg-yellow-400 text-black font-bold uppercase tracking-wide px-5 py-3 hover:bg-yellow-500 transition">Add Category</button>
      </form>
      <div className="xl:col-span-6 space-y-3">
        <h3 className="font-display font-bold text-xl text-[var(--text-primary)] mb-4">All Categories <span className="text-zinc-500 text-base">({cats.length})</span></h3>
        {cats.map((c) => (
          <div key={c.id} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-4 flex items-center gap-4 hover:border-yellow-400/20 transition">
            <div className="flex-1">
              <div className="font-display font-bold text-[var(--text-primary)]">{c.name}</div>
              <div className="text-xs font-mono text-yellow-400/60 mt-0.5">/{c.slug}</div>
              {c.description && <p className="text-sm text-zinc-400 mt-1">{c.description}</p>}
            </div>
            <button onClick={() => del(c.id)} className="text-zinc-400 hover:text-red-400 transition"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Polls ----------
function PollsAdmin() {
  const [polls, setPolls] = useState([]);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [endsAt, setEndsAt] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/polls"); setPolls(data); } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const addOption = () => setOptions([...options, ""]);
  const removeOption = (i) => { if (options.length <= 2) return; setOptions(options.filter((_, idx) => idx !== i)); };
  const updateOption = (i, val) => { const updated = [...options]; updated[i] = val; setOptions(updated); };

  const save = async (e) => {
    e.preventDefault();
    const filtered = options.filter((o) => o.trim());
    if (filtered.length < 2) return toast.error("At least 2 options required");
    setSaving(true);
    try {
      await api.post("/polls", { question: question.trim(), options: filtered, ends_at: endsAt || null });
      toast.success("Poll created and set as active!");
      setQuestion(""); setOptions(["", ""]); setEndsAt("");
      load();
    } catch (_) { toast.error("Could not create poll"); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this poll and all votes?")) return;
    await api.delete(`/polls/${id}`);
    toast.success("Poll deleted");
    load();
  };

  const totalVotes = (poll) => poll.options.reduce((sum, o) => sum + o.votes, 0);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
      <form onSubmit={save} className="xl:col-span-6 bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6 space-y-4">
        <h3 className="font-display font-bold text-xl text-[var(--text-primary)]">Create New Poll</h3>
        <p className="text-xs text-[var(--text-tertiary)] font-mono">Creating a new poll will automatically deactivate the current one.</p>
        <div className="space-y-1">
          <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widests">Question *</label>
          <input required placeholder="e.g. Will Bitcoin break $80K by May 31?" value={question} onChange={(e) => setQuestion(e.target.value)} className="w-full bg-[var(--bg-base)] border border-[var(--border-soft)] px-3 py-2 text-[var(--text-primary)] focus:border-yellow-400 outline-none" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widests">Options *</label>
            <button type="button" onClick={addOption} className="text-xs font-mono text-yellow-400 hover:underline">+ Add Option</button>
          </div>
          {options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <input required placeholder={"Option " + (i + 1)} value={opt} onChange={(e) => updateOption(i, e.target.value)} className="flex-1 bg-[#050505] border border-white/20 px-3 py-2 text-white focus:border-yellow-400 outline-none" />
              {options.length > 2 && <button type="button" onClick={() => removeOption(i)} className="text-zinc-500 hover:text-red-400 transition"><X className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
        <div className="space-y-1">
          <label className="text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widests">End Date (optional)</label>
          <input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className="w-full bg-[var(--bg-base)] border border-[var(--border-soft)] px-3 py-2 text-[var(--text-primary)] focus:border-yellow-400 outline-none" />
        </div>
        <button type="submit" disabled={saving} className="w-full bg-yellow-400 text-black font-bold uppercase tracking-wide px-5 py-3 hover:bg-yellow-500 transition disabled:opacity-50">
          {saving ? "Creating..." : "Create & Activate Poll"}
        </button>
      </form>
      <div className="xl:col-span-6 space-y-4">
        <h3 className="font-display font-bold text-xl text-[var(--text-primary)] mb-4">All Polls <span className="text-[var(--text-tertiary)] text-base">({polls.length})</span></h3>
        {polls.length === 0 && <p className="text-zinc-500 font-mono text-sm">No polls yet. Create one!</p>}
        {polls.map((poll) => (
          <div key={poll.id} className={"bg-[var(--bg-panel)] border p-5 " + (poll.active ? "border-yellow-400/40" : "border-[var(--border-soft)]")}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                {poll.active && <span className="text-xs font-mono text-yellow-400 uppercase tracking-widest bg-yellow-400/10 px-2 py-1 mb-2 inline-block">● Active</span>}
                <h4 className="font-display font-bold text-[var(--text-primary)] mt-1">{poll.question}</h4>
                <p className="text-xs font-mono text-[var(--text-tertiary)] mt-1">{totalVotes(poll)} total votes{poll.ends_at && " · Ends " + new Date(poll.ends_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => del(poll.id)} className="text-zinc-400 hover:text-red-400 transition flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2">
              {poll.options.map((opt, i) => {
                const total = totalVotes(poll);
                const pct = total > 0 ? Math.round((opt.votes / total) * 100) : 0;
                const colors = ["bg-yellow-400", "bg-blue-500", "bg-green-500", "bg-red-500", "bg-purple-500"];
                return (
                  <div key={opt.label}>
                    <div className="flex justify-between text-xs font-mono mb-1">
                      <span className="text-zinc-300">{opt.label}</span>
                      <span className="text-white">{pct}% ({opt.votes})</span>
                    </div>
                    <div className="w-full bg-[var(--bg-elevated)] h-4">
                      <div className={"h-full " + colors[i % colors.length]} style={{ width: pct + "%" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Projects ----------
function ProjectsAdmin() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/projects");
      setProjects(data);
    } catch (_) {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/projects/${id}`, { status });
    toast.success("Status updated to " + status);
    load();
  };

  const toggleFeatured = async (p) => {
    await api.put(`/projects/${p.id}`, { featured: !p.featured });
    toast.success(p.featured ? "Removed from featured" : "Marked as featured");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    load();
  };

  const STATUS_COLORS = {
    pending: "text-zinc-400 bg-zinc-400/10",
    live: "text-green-400 bg-green-400/10",
    upcoming: "text-yellow-400 bg-yellow-400/10",
    rejected: "text-red-400 bg-red-400/10",
  };

  if (loading) return <p className="text-[var(--text-tertiary)] font-mono text-sm">Loading projects...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-display font-bold text-xl text-[var(--text-primary)]">
          Project Applications <span className="text-[var(--text-tertiary)] text-base">({projects.length})</span>
        </h3>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-20 bg-[var(--bg-panel)] border border-[var(--border-soft)]">
          <Rocket className="w-12 h-12 text-[var(--text-tertiary)] mx-auto mb-4" />
          <p className="text-[var(--text-tertiary)] font-mono">No project applications yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {projects.map((p) => (
            <div key={p.id} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-6 hover:border-yellow-400/20 transition">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-4">
                  {p.logo_url ? (
                    <img src={p.logo_url} alt={p.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-yellow-400 font-bold">{p.name[0]}</span>
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-display font-bold text-lg text-[var(--text-primary)]">{p.name}</h4>
                      {p.ticker && <span className="text-xs font-mono text-[var(--text-tertiary)]">${p.ticker}</span>}
                      <span className={`text-xs font-mono uppercase px-2 py-0.5 rounded-sm ${STATUS_COLORS[p.status] || STATUS_COLORS.pending}`}>{p.status}</span>
                      {p.featured && <span className="text-xs font-mono text-yellow-400 bg-yellow-400/10 px-2 py-0.5">★ Featured</span>}
                    </div>
                    <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-xl">{p.description}</p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <span className="text-xs font-mono text-[var(--text-tertiary)] bg-[var(--bg-elevated)] px-2 py-1">{p.category}</span>
                      {p.launch_date && <span className="text-xs font-mono text-[var(--text-tertiary)]">Launch: {p.launch_date}</span>}
                      <span className="text-xs font-mono text-[var(--text-tertiary)]">Contact: {p.contact_email}</span>
                      {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-yellow-400 hover:underline">{p.website}</a>}
                    </div>
                    {(p.twitter || p.telegram || p.discord) && (
                      <div className="flex items-center gap-3 mt-1">
                        {p.twitter && <a href={p.twitter} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-blue-400 hover:underline">Twitter</a>}
                        {p.telegram && <a href={p.telegram} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-blue-400 hover:underline">Telegram</a>}
                        {p.discord && <a href={p.discord} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-blue-400 hover:underline">Discord</a>}
                      </div>
                    )}
                    <p className="text-xs font-mono text-[var(--text-tertiary)] mt-2">Applied: {new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
                  <button onClick={() => updateStatus(p.id, "upcoming")} className="text-xs font-mono px-3 py-1.5 border border-yellow-400/30 text-yellow-400 hover:bg-yellow-400/10 transition">Upcoming</button>
                  <button onClick={() => updateStatus(p.id, "live")} className="text-xs font-mono px-3 py-1.5 border border-green-400/30 text-green-400 hover:bg-green-400/10 transition">Live</button>
                  <button onClick={() => updateStatus(p.id, "rejected")} className="text-xs font-mono px-3 py-1.5 border border-red-400/30 text-red-400 hover:bg-red-400/10 transition">Reject</button>
                  <button onClick={() => toggleFeatured(p)} className="text-xs font-mono px-3 py-1.5 border border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400 hover:text-yellow-400 transition">
                    <Star className="w-3 h-3 inline mr-1" />{p.featured ? "Unfeature" : "Feature"}
                  </button>
                  <button onClick={() => del(p.id)} className="text-zinc-400 hover:text-red-400 transition"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ---------- Predictions ----------
function PredictionsAdmin() {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/community/posts?limit=100");
      setPredictions(data.filter((p) => p.is_prediction));
    } catch (_) {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resolve = async (id) => {
    setResolving(id);
    try {
      const { data } = await api.post(`/community/predictions/${id}/resolve`);
      toast.success(`Resolved: ${data.correct ? "✓ Correct" : "✗ Wrong"} — Price was $${data.current_price.toLocaleString()}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve");
    } finally { setResolving(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this prediction?")) return;
    await api.delete(`/community/posts/${id}`);
    toast.success("Prediction deleted");
    load();
  };

  if (loading) return <p className="text-[var(--text-tertiary)] font-mono text-sm">Loading predictions...</p>;

  const pending = predictions.filter((p) => !p.resolved);
  const resolved = predictions.filter((p) => p.resolved);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-display font-bold text-xl text-[var(--text-primary)]">
          Predictions <span className="text-[var(--text-tertiary)] text-base">({predictions.length} total)</span>
        </h3>
        <div className="flex items-center gap-3 text-xs font-mono">
          <span className="text-yellow-400">{pending.length} pending</span>
          <span className="text-green-400">{resolved.filter(p => p.correct).length} correct</span>
          <span className="text-red-400">{resolved.filter(p => !p.correct).length} wrong</span>
        </div>
      </div>

      {predictions.length === 0 ? (
        <div className="text-center py-20 bg-[var(--bg-panel)] border border-[var(--border-soft)]">
          <p className="text-[var(--text-tertiary)] font-mono">No predictions posted yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Pending first */}
          {pending.length > 0 && (
            <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-3">
              Pending Resolution ({pending.length})
            </div>
          )}
          {pending.map((p) => (
            <div key={p.id} className="bg-[var(--bg-panel)] border border-yellow-400/20 p-4 hover:border-yellow-400/40 transition">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-xs font-mono px-2 py-0.5 bg-yellow-400/10 border border-yellow-400/30 text-yellow-400">
                      {p.asset} {p.direction} ${Number(p.target_price).toLocaleString()}
                    </span>
                    <span className="text-xs font-mono text-[var(--text-tertiary)]">
                      by {new Date(p.target_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                    <span className="text-xs font-mono text-zinc-400 border border-[var(--border-soft)] px-2 py-0.5">Pending</span>
                  </div>
                  <div className="font-display font-bold text-[var(--text-primary)] truncate">{p.title}</div>
                  <div className="text-xs font-mono text-zinc-500 mt-1">
                    By {p.author_name} · {new Date(p.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => resolve(p.id)}
                    disabled={resolving === p.id}
                    className="text-xs font-mono px-3 py-1.5 border border-green-400/30 text-green-400 hover:bg-green-400/10 transition disabled:opacity-50"
                  >
                    {resolving === p.id ? "Resolving..." : "Resolve Now"}
                  </button>
                  <button onClick={() => del(p.id)} className="text-zinc-400 hover:text-red-400 transition">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Resolved */}
          {resolved.length > 0 && (
            <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mt-6 mb-3">
              Resolved ({resolved.length})
            </div>
          )}
          {resolved.map((p) => (
            <div key={p.id} className={"bg-[var(--bg-panel)] border p-4 transition " + (p.correct ? "border-green-400/20" : "border-red-400/20")}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-xs font-mono px-2 py-0.5 bg-[var(--bg-elevated)] border border-[var(--border-soft)] text-[var(--text-tertiary)]">
                      {p.asset} {p.direction} ${Number(p.target_price).toLocaleString()}
                    </span>
                    <span className={`text-xs font-mono px-2 py-0.5 border ${p.correct ? "text-green-400 border-green-400/30 bg-green-400/10" : "text-red-400 border-red-400/30 bg-red-400/10"}`}>
                      {p.correct ? "✓ Correct" : "✗ Wrong"}
                    </span>
                    {p.resolved_price && (
                      <span className="text-xs font-mono text-[var(--text-tertiary)]">
                        Resolved at ${Number(p.resolved_price).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="font-display font-bold text-[var(--text-primary)] truncate">{p.title}</div>
                  <div className="text-xs font-mono text-zinc-500 mt-1">
                    By {p.author_name} · {new Date(p.created_at).toLocaleDateString()}
                  </div>
                </div>
                <button onClick={() => del(p.id)} className="text-zinc-400 hover:text-red-400 transition flex-shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}